import { c as _c } from "react/compiler-runtime";
import { useRouter } from "next/router";
import Link from "next/link";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductCard } from "@/components/ProductCard";
import { ProductsSidebar } from "@/components/ProductsSidebar";
import type { MessageKey } from "@/lib/i18n";
import { normalizeLocale, t } from "@/lib/i18n";
import type { ProductBrand, ProductCategory, MilitaryFaction, MilitarySubcategory, ProductSortOption } from "@/data/products";
import { filterProducts } from "@/data/products";
import { stripInternalRouteQuery } from "@/lib/publicPath";
import { getAllProductsUrl, getCategoryUrl, getMilitarySubcategoryUrl } from "@/lib/urls";
import styles from "@/styles/Home.module.css";
import productListStyles from "@/styles/ProductList.module.css";
const PRODUCTS_PER_PAGE = 12;
const SORT_OPTIONS: {
  value: ProductSortOption;
  labelKey: MessageKey;
}[] = [{
  value: "default",
  labelKey: "products.sort.default"
}, {
  value: "number_desc",
  labelKey: "products.sort.numberDesc"
}, {
  value: "number_asc",
  labelKey: "products.sort.numberAsc"
}, {
  value: "pieces_desc",
  labelKey: "products.sort.piecesDesc"
}, {
  value: "pieces_asc",
  labelKey: "products.sort.piecesAsc"
}];
function isValidSort(v: unknown): v is ProductSortOption {
  return v === "default" || v === "number_asc" || v === "number_desc" || v === "pieces_asc" || v === "pieces_desc";
}
export interface ProductsListContentProps {
  locale: string;
  pageTitle: string;
  pageSubtitle: string;
  seoTitle: string;
  brands?: ProductBrand[] | null;
  category?: ProductCategory | null;
  subcategory?: MilitarySubcategory | null;
  searchQuery?: string | null;
  factions?: MilitaryFaction[] | null;
  scales?: string[] | null;
  piecesMin?: number | null;
  piecesMax?: number | null;
  pageBasePath: string;
}
export function ProductsListContent(t0) {
  const $ = _c(60);
  const {
    locale: localeProp,
    pageTitle,
    pageSubtitle,
    seoTitle,
    brands: t1,
    category,
    subcategory,
    searchQuery: t2,
    factions: t3,
    scales: t4,
    piecesMin,
    piecesMax,
    pageBasePath
  } = t0;
  const brands = t1 === undefined ? [] : t1;
  const searchQuery = t2 === undefined ? "" : t2;
  const factions = t3 === undefined ? [] : t3;
  const scales = t4 === undefined ? [] : t4;
  const router = useRouter();
  const locale = normalizeLocale(localeProp);
  const query = router.query;
  let t5;
  if ($[0] !== query.sort) {
    t5 = isValidSort(query.sort) ? query.sort : "default";
    $[0] = query.sort;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const sort = t5;
  const pageRaw = typeof query.page === "string" ? parseInt(query.page, 10) : 1;
  let t6;
  if ($[2] !== pageRaw) {
    t6 = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;
    $[2] = pageRaw;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const page = t6;
  const filtered = filterProducts({
    searchQuery: searchQuery && searchQuery.trim() ? searchQuery.trim() : undefined,
    brands: Array.isArray(brands) && brands.length > 0 ? brands : undefined,
    category: category ?? undefined,
    militarySubcategory: subcategory ?? undefined,
    factions: Array.isArray(factions) && factions.length > 0 ? factions : undefined,
    scales: Array.isArray(scales) && scales.length > 0 ? scales : undefined,
    piecesMin: piecesMin ?? undefined,
    piecesMax: piecesMax ?? undefined
  }, sort);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PRODUCTS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = filtered.slice(start, start + PRODUCTS_PER_PAGE);
  let t7;
  bb0: {
    const home = {
      label: t(locale, "nav.home"),
      href: "/"
    };
    const sets = {
      label: t(locale, "nav.products"),
      href: getAllProductsUrl(locale)
    };
    if (!category) {
      t7 = [home, {
        label: t(locale, "nav.products")
      }];
      break bb0;
    }
    const cat = {
      label: t(locale, `category.${category}`),
      href: getCategoryUrl(category, locale)
    };
    if (!subcategory) {
      t7 = [home, sets, {
        label: pageTitle
      }];
      break bb0;
    }
    t(locale, `militarySub.${subcategory}`);
    getMilitarySubcategoryUrl(subcategory, locale);
    let t8;
    if ($[4] !== pageTitle) {
      t8 = {
        label: pageTitle
      };
      $[4] = pageTitle;
      $[5] = t8;
    } else {
      t8 = $[5];
    }
    t7 = [home, sets, cat, t8];
  }
  const breadcrumbItems = t7;
  const baseQuery = new URLSearchParams();
  if (searchQuery) {
    baseQuery.set("q", searchQuery);
  }
  if (brands?.length) {
    baseQuery.set("brands", brands.join(","));
  }
  if (factions?.length) {
    baseQuery.set("faction", factions.join(","));
  }
  if (scales?.length) {
    baseQuery.set("scale", scales.join(","));
  }
  if (piecesMin != null) {
    baseQuery.set("piecesMin", String(piecesMin));
  }
  if (piecesMax != null) {
    baseQuery.set("piecesMax", String(piecesMax));
  }
  if (sort !== "default") {
    baseQuery.set("sort", sort);
  }
  const queryPrefix = baseQuery.toString() ? `${baseQuery.toString()}&` : "";
  const isPaginated = safePage > 1;
  let t8;
  if ($[6] !== isPaginated || $[7] !== searchQuery) {
    t8 = isPaginated || Boolean(searchQuery?.trim());
    $[6] = isPaginated;
    $[7] = searchQuery;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== pageBasePath || $[10] !== seoTitle || $[11] !== t8) {
    t9 = <SEO title={seoTitle} canonicalPath={pageBasePath} noIndex={t8} />;
    $[9] = pageBasePath;
    $[10] = seoTitle;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      maxWidth: 1280,
      margin: "0 auto 1rem"
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== breadcrumbItems || $[15] !== locale) {
    t11 = <div style={t10}><Breadcrumbs items={breadcrumbItems} locale={locale} /></div>;
    $[14] = breadcrumbItems;
    $[15] = locale;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = productListStyles;
  let t13;
  if ($[17] !== pageTitle) {
    t13 = <h1 className={styles.sectionTitle}>{pageTitle}</h1>;
    $[17] = pageTitle;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== pageSubtitle) {
    t14 = <p className={styles.sectionSubtitle}>{pageSubtitle}</p>;
    $[19] = pageSubtitle;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t13 || $[22] !== t14) {
    t15 = <div>{t13}{t14}</div>;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== locale) {
    t16 = t(locale, "products.sort.label");
    $[24] = locale;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t16) {
    t17 = <label htmlFor="sort-select" className={productListStyles.sortLabel}>{t16}:</label>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== pageBasePath || $[29] !== router) {
    t18 = e => {
      const v = e.target.value as ProductSortOption;
      if (isValidSort(v)) {
        const q = new URLSearchParams(stripInternalRouteQuery((router.asPath || "").split("?")[1] || "").replace(/^\?/, ""));
        q.set("sort", v);
        q.set("page", "1");
        router.push(`${pageBasePath}?${q.toString()}`, undefined, {
          scroll: false
        });
      }
    };
    $[28] = pageBasePath;
    $[29] = router;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== locale) {
    t19 = SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{t(locale, opt.labelKey)}</option>);
    $[31] = locale;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== sort || $[34] !== t18 || $[35] !== t19) {
    t20 = <select id="sort-select" value={sort} onChange={t18} className={productListStyles.sortSelect}>{t19}</select>;
    $[33] = sort;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t17 || $[38] !== t20) {
    t21 = <div className={productListStyles.sortRow}>{t17}{t20}</div>;
    $[37] = t17;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t15 || $[41] !== t21) {
    t22 = <div className={productListStyles.productsHeader}>{t15}{t21}</div>;
    $[40] = t15;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = productListStyles;
  let t24;
  if ($[43] !== locale) {
    t24 = product => <ProductCard key={product.id} product={product} locale={locale} />;
    $[43] = locale;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const t25 = paginatedProducts.map(t24);
  let t26;
  if ($[45] !== t23.productsGrid || $[46] !== t25) {
    t26 = <div className={t23.productsGrid}>{t25}</div>;
    $[45] = t23.productsGrid;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== filtered.length || $[49] !== locale || $[50] !== pageBasePath || $[51] !== queryPrefix || $[52] !== safePage || $[53] !== totalPages) {
    t27 = filtered.length === 0 ? <p className={styles.sectionSubtitle} style={{
      marginTop: "1rem"
    }}>{t(locale, "products.empty")}</p> : totalPages > 1 ? <nav className={productListStyles.pager} aria-label={t(locale, "products.pagerLabel")}>{safePage > 1 && <Link href={`${pageBasePath}?${queryPrefix}page=${safePage - 1}`} className={productListStyles.pagerLink}>← {t(locale, "products.pagerPrev")}</Link>}<span className={productListStyles.pagerInfo}>{t(locale, "products.pagerPage")} {safePage}{" "}{t(locale, "products.pagerOf")} {totalPages} ({filtered.length} {t(locale, "products.pagerCount")})</span>{safePage < totalPages && <Link href={`${pageBasePath}?${queryPrefix}page=${safePage + 1}`} className={productListStyles.pagerLink}>{t(locale, "products.pagerNext")} →</Link>}</nav> : null;
    $[48] = filtered.length;
    $[49] = locale;
    $[50] = pageBasePath;
    $[51] = queryPrefix;
    $[52] = safePage;
    $[53] = totalPages;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== t12.productsMain || $[56] !== t22 || $[57] !== t26 || $[58] !== t27) {
    t28 = <div className={t12.productsMain}>{t22}{t26}{t27}</div>;
    $[55] = t12.productsMain;
    $[56] = t22;
    $[57] = t26;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  return <div className={styles.page}>{t9}<Layout><main className={styles.main}><section className={styles.sectionBestsellers}>{t11}<div className={productListStyles.productsLayout}><div className={productListStyles.productsSidebarCol}><ProductsSidebar locale={locale} category={category} subcategory={subcategory} searchQuery={searchQuery} brands={brands} factions={factions} scales={scales} piecesMin={piecesMin} piecesMax={piecesMax} /></div>{t28}</div></section></main></Layout></div>;
}